import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable, LOCALE_ID, Signal, inject, signal } from "@angular/core";
import { MessageService } from "primeng/api";
import { Observable, catchError, defer, finalize, map, throwError } from "rxjs";
import { SUPPRESS_GLOBAL_ERROR_DIALOG } from "src/app/shared/interceptors/http.interceptor";

/**
 * API endpoint listing the documents published for the release this deployment serves;
 * `${DOCUMENTATION_ENDPOINT}/{stem}?locale=` streams one of them as a PDF.
 *
 * The path is relative and contains `/api/`, so `APIInterceptorService` rewrites it onto the API host
 * and `AuthInterceptorService` attaches the bearer token. The release is resolved server-side; the
 * `v1` here is the API version, not the release.
 */
export const DOCUMENTATION_ENDPOINT = "/api/v1/documentation";

/**
 * Body of `GET /api/v1/documentation`: the stems of the published documents, in the order the Help
 * menu lists them.
 */
export interface ListDocumentsResponse {
  documents: string[];
}

const PDF_MIME_TYPE = "application/pdf";

@Injectable({ providedIn: "root" })
export class GuideDownloadService {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly localeId = inject(LOCALE_ID);

  private readonly opening = signal<ReadonlySet<string>>(new Set());

  /**
   * Stems with a request in flight, for callers that show the wait on the control that started it.
   */
  readonly openingStems: Signal<ReadonlySet<string>> = this.opening.asReadonly();

  /**
   * Whether a request for `stem` is in flight. Reads `openingStems`, so it is tracked inside `effect`
   * and `computed`.
   */
  isOpening(stem: string): boolean {
    return this.openingStems().has(stem);
  }

  /**
   * Lists the stems of the documents published for this release. Errors propagate to the caller.
   *
   * The request opts out of the app-wide error dialog: the caller reports the failure itself and
   * renders the rest of the menu, and the dialog would otherwise tell the user to refresh a page that
   * works, on every load.
   */
  list(): Observable<string[]> {
    const context = new HttpContext().set(SUPPRESS_GLOBAL_ERROR_DIALOG, true);

    return this.http.get<ListDocumentsResponse>(DOCUMENTATION_ENDPOINT, { context }).pipe(map(({ documents }) => documents));
  }

  /**
   * Opens the document `stem` in a new tab in the active locale, reporting progress and failure to
   * the user under `label`.
   *
   * A document that has not been published for this release answers 404, which is an expected state
   * because documents are published independently of deployment. The request can outlast the menu
   * that started it, which closes on the click, so progress and failure are both announced on the
   * toast.
   *
   * A call for a stem whose request is already in flight is ignored, per the AGENTS.md requirement
   * that UI-triggered events prevent duplicate submissions. The guard is per stem, so an open on one
   * document leaves the others clickable.
   */
  open(stem: string, label: string): void {
    if (this.isOpening(stem)) {
      return;
    }

    this.opening.update((stems) => new Set(stems).add(stem));

    this.messageService.add({
      severity: "info",
      summary: $localize`:@@guideOpeningTitle:Opening ${label}:label:`,
      detail: $localize`:@@guideOpeningDescription:The document will open in a new tab.`
    });

    this.fetch(stem, label)
      .pipe(
        finalize(() =>
          this.opening.update((stems) => {
            const remaining = new Set(stems);
            remaining.delete(stem);
            return remaining;
          })
        )
      )
      .subscribe({
        error: () =>
          this.messageService.add({
            severity: "error",
            summary: $localize`:@@guideUnavailableTitle:${label}:label: unavailable`,
            detail: $localize`:@@guideUnavailableDescription:The document could not be opened. Please contact support if this continues.`
          })
      });
  }

  /**
   * Fetches the document and hands it to a new browser tab.
   *
   * The tab is opened before the request is issued, while the click that triggered it is still the
   * active user gesture. A `window.open` called from the response handler counts as an unsolicited
   * popup and is blocked, so the blank tab is claimed first and pointed at the document once it
   * arrives. A blocked popup falls back to saving the file as `${label}.pdf`, and a failed request
   * closes the tab again rather than leaving an empty one behind.
   *
   * The object URL survives the tab branch on purpose: it has been handed to another document that
   * may not have finished reading it, and the browser releases it when this document unloads. The
   * save branch consumes it synchronously in this document, so it is revoked right after the click.
   *
   * The request opts out of the app-wide error dialog so an unpublished document is reported once,
   * on the toast. The dialog would otherwise fire alongside it and tell the user to refresh the page.
   */
  private fetch(stem: string, label: string): Observable<void> {
    return defer(() => {
      const tab = window.open("", "_blank");
      const context = new HttpContext().set(SUPPRESS_GLOBAL_ERROR_DIALOG, true);

      return this.http
        .get(`${DOCUMENTATION_ENDPOINT}/${stem}`, {
          params: { locale: this.localeId },
          responseType: "blob",
          context
        })
        .pipe(
          map((blob) => {
            const objectUrl = URL.createObjectURL(this.asPdf(blob));

            if (tab) {
              tab.location.href = objectUrl;
              return;
            }

            const anchor = document.createElement("a");
            anchor.href = objectUrl;
            anchor.download = `${label}.pdf`;
            anchor.click();
            URL.revokeObjectURL(objectUrl);
          }),
          catchError((error: unknown) => {
            tab?.close();
            return throwError(() => error);
          })
        );
    });
  }

  /**
   * Re-types the response as a PDF instead of trusting the server's Content-Type.
   *
   * A blob: URL inherits the origin of the document that minted it, so a response typed as anything
   * the browser renders — HTML above all — would execute in the app's own origin once the new tab
   * navigates to it. Pinning the type keeps the tab a document viewer no matter what the endpoint
   * sends.
   */
  private asPdf(blob: Blob): Blob {
    return blob.type === PDF_MIME_TYPE ? blob : new Blob([blob], { type: PDF_MIME_TYPE });
  }
}
